import { Input, MenuFlyout, MenuOption, MenuSection } from "@design";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BrandSummary } from "@/api";
import { Text } from "@/components/Text";

/** Above this many brands the flyout gets a filter field (as AssistantMenu). */
const BRAND_FILTER_THRESHOLD = 7;

function BrandOptions({
  brands,
  selected,
  onSelect,
}: Readonly<{ brands: BrandSummary[]; selected: string | null; onSelect: (brandUuid: string | null) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  const { t: tBrands } = useTranslation("brands");
  // internal/external are enum values (translated as in Build → Brands); anything else is the author's own text.
  const audience = (value: string | null | undefined) =>
    value === "internal" || value === "external" ? tBrands(`audience.${value}`) : (value ?? undefined);
  const [filter, setFilter] = useState("");
  const query = filter.trim().toLowerCase();
  const shown = query ? brands.filter((b) => `${b.name} ${b.audience ?? ""}`.toLowerCase().includes(query)) : brands;
  return (
    <MenuSection title={t("brandSection")}>
      {brands.length > BRAND_FILTER_THRESHOLD && (
        <div className="px-1 pb-1">
          <Input
            size="sm"
            leadingIcon={<Search size={14} />}
            aria-label={t("filterBrands")}
            placeholder={t("filterBrands")}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            // The menu's own keys (Home/End/Space) would otherwise hijack typing; arrows still move into the list.
            onKeyDown={(event) => {
              if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Escape") {
                event.stopPropagation();
              }
            }}
          />
        </div>
      )}
      <MenuOption
        description={t("defaultBrandDescription")}
        selected={selected === null}
        onSelect={() => onSelect(null)}
      >
        {t("defaultBrand")}
      </MenuOption>
      {shown.map((brand) => (
        <MenuOption
          key={brand.uuid}
          description={audience(brand.audience)}
          selected={selected === brand.uuid}
          onSelect={() => onSelect(brand.uuid)}
        >
          {brand.name}
        </MenuOption>
      ))}
      {shown.length === 0 && (
        <Text variant="caption" tone="tertiary" className="px-3 py-2">
          {t("noBrandsMatch")}
        </Text>
      )}
    </MenuSection>
  );
}

/**
 * The composer's "More options" menu: the chat's brand (frontend/'s BrandPicker). Picked before the chat starts and
 * saved on it; read-only afterwards. Null = the company default brand.
 */
export function MoreMenu({
  brands,
  selected,
  onSelect,
  fixedBrandName,
}: Readonly<{
  brands: BrandSummary[];
  selected: string | null;
  onSelect: (brandUuid: string | null) => void;
  /** Once the chat has started its brand can't change: show it read-only (undefined while the chat is new). */
  fixedBrandName?: string | null;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  if (fixedBrandName !== undefined) {
    return (
      <MenuSection title={t("brandSection")}>
        <MenuOption description={t("brandFixed")}>{fixedBrandName ?? t("defaultBrand")}</MenuOption>
      </MenuSection>
    );
  }
  const current = brands.find((b) => b.uuid === selected)?.name ?? t("defaultBrand");
  return (
    <MenuFlyout value={current} flyout={<BrandOptions brands={brands} selected={selected} onSelect={onSelect} />}>
      {t("brand")}
    </MenuFlyout>
  );
}
